import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Buildings, PlusCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const STATUS = {
  submitted: { label: "Submitted", tone: "bg-slate-100 text-slate-700", note: "Your suggestion has been received." },
  researching: { label: "Researching", tone: "bg-blue-100 text-blue-700", note: "Our team is researching the institution." },
  information_needed: { label: "Info needed", tone: "bg-amber-100 text-amber-700", note: "We need additional information to verify this institution." },
  under_review: { label: "Under review", tone: "bg-indigo-100 text-indigo-700", note: "The available information is being reviewed." },
  approved: { label: "Approved", tone: "bg-emerald-100 text-emerald-700", note: "Approved for the AlumniPulse directory." },
  added: { label: "Added", tone: "bg-emerald-600 text-white", note: "The institution is now available on AlumniPulse™." },
  duplicate: { label: "Already listed", tone: "bg-slate-100 text-slate-700", note: "We found an existing institution that matches your suggestion." },
  not_verified: { label: "Not verified", tone: "bg-rose-100 text-rose-700", note: "We were unable to sufficiently verify the institution at this time." },
  archived: { label: "Archived", tone: "bg-slate-100 text-slate-500", note: "Research paused — can be revisited with more information." },
};

export default function MySuggestions() {
  useSeo("My Institution Suggestions", "Track the institutions you've suggested to AlumniPulse.");
  const [items, setItems] = useState(null);

  useEffect(() => { api.get("/institution-suggestions/mine").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  return (
    <main className="mx-auto max-w-3xl px-5 py-10" data-testid="my-suggestions-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-secondary mb-1">Your contributions</p>
          <h1 className="font-heading text-3xl font-light tracking-tight">Institution suggestions</h1>
          <p className="mt-2 text-sm text-muted-foreground">Track the institutions you've helped bring into the global AlumniPulse network.</p>
        </div>
        <Link to="/institutions/suggest"><Button className="rounded-full gap-2" data-testid="suggest-another"><PlusCircle size={16} weight="fill" /> Suggest an institution</Button></Link>
      </div>

      {items === null ? <p className="mt-10 text-muted-foreground">Loading…</p>
        : items.length === 0 ? (
          <Card className="mt-8 border-border/60 p-10 text-center">
            <Buildings size={40} weight="duotone" className="mx-auto text-muted-foreground" />
            <p className="mt-3 text-muted-foreground">You haven't suggested any institutions yet.</p>
            <Link to="/institutions/suggest"><Button className="mt-4 rounded-full">Suggest your first</Button></Link>
          </Card>
        ) : (
          <div className="mt-8 space-y-3">
            {items.map((s) => {
              const st = STATUS[s.status] || STATUS.submitted;
              return (
                <Card key={s.id} className="border-border/60 p-4" data-testid={`my-suggestion-${s.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading text-lg font-medium">{s.name}</h3>
                        <Badge className={`rounded-full font-normal ${st.tone}`}>{st.label}</Badge>
                      </div>
                      <p className="mt-0.5 text-sm text-muted-foreground">{[s.city || s.locality, s.state, s.country].filter(Boolean).join(", ")}</p>
                      <p className="mt-2 text-sm text-muted-foreground">{s.status_note || st.note}</p>
                    </div>
                    {s.institution_id && (
                      <Link to={`/institutions/${s.institution_id}`}><Button size="sm" variant="outline" className="rounded-full">View institution</Button></Link>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
    </main>
  );
}
